import type { Board } from "../board";
import { CircleEffect } from "../effects/circleEffect";
import { Hit } from "../effects/hit";
import { Event } from "../events/event";
import { EventType } from "../events/eventType";
import { HostilityCalculator, HostilityFactor } from "../hostilityCalculator";
import { PRIORITY_LAST, type ProcessableThing } from "../interfaces";
import type { Mob } from "../mob";
import { Horned } from "../mobs/horned";
import { Slugant } from "../mobs/slugant";
import { Squid } from "../mobs/squid";
import { randomChance, randomElement, randomInt } from "../random";
// LevelDirector has no XY — it's a non-physical Thing
import { Thing } from "../thing";
import { XY } from "../xy";
import { Rock } from "./rock";
import { RockMobCave } from "./rockMobCave";
import { MobSpawnerConfig } from "./portal2/mobSpawnerConfig";
import { Portal2 } from "./portal2/portal2";

export class LevelDirector extends Thing implements ProcessableThing {
	static key = "LevelDirector";
	private runAtTurnCount: number = 0;
	private isFirstRun: boolean = true;
	private maxHostilityNotified: number = 0;
	onlySpawnCaves: boolean = false;
	private mobSpawnerConfig: MobSpawnerConfig | null = null;
	private hostilityCalculator: HostilityCalculator | null = null;

	getProcessPriority(): number {
		return PRIORITY_LAST;
	}

	processTurn(board: Board): void {
		if (!board.player) return;

		if (this.isFirstRun || !this.hostilityCalculator) {
			this.isFirstRun = false;
			this.hostilityCalculator = this.configCalculator(board);
		}

		if (this.runAtTurnCount <= board.turnCount) {
			this.scheduleNextRun(board);
			this.run(board);
		}
	}

	private configCalculator(board: Board): HostilityCalculator {
		const hc = new HostilityCalculator(board);
		hc.setFactorByPercentage(HostilityFactor.MOBS_KILLED, 0.4);
		hc.setFactorByPercentage(HostilityFactor.MOBS_SOLIDIFIED, 0.4);
		hc.setFactorByPercentage(HostilityFactor.PORTALS_DESTROYED, 3);
		hc.setFactorByPercentage(HostilityFactor.PORTALS_ALIVE, -15);
		hc.setFactorByPercentage(HostilityFactor.MOBS_ALIVE, -3);
		hc.setFactorByPercentage(HostilityFactor.TURN_COUNT, 0.02);
		return hc;
	}

	private scheduleNextRun(board: Board): void {
		this.runAtTurnCount = board.turnCount + randomInt(8, 12);
	}

	private run(board: Board): void {
		const hostility = this.hostilityCalculator!.calculate();
		this.recordHostilityCalculated(hostility, board);
		this.act(hostility, board);
	}

	private recordHostilityCalculated(hostility: number, board: Board): void {
		if (this.maxHostilityNotified >= hostility) return;
		if (hostility > 0.8) {
			board.addEvent(new Event(EventType.LEVEL_DIRECTOR_HOSTILITY_REACHED_5));
			this.maxHostilityNotified = 1;
		} else if (hostility > 0.6) {
			board.addEvent(new Event(EventType.LEVEL_DIRECTOR_HOSTILITY_REACHED_4));
			this.maxHostilityNotified = 0.8;
		} else if (hostility > 0.4) {
			board.addEvent(new Event(EventType.LEVEL_DIRECTOR_HOSTILITY_REACHED_3));
			this.maxHostilityNotified = 0.6;
		} else if (hostility > 0.2) {
			board.addEvent(new Event(EventType.LEVEL_DIRECTOR_HOSTILITY_REACHED_2));
			this.maxHostilityNotified = 0.4;
		} else if (hostility > 0) {
			board.addEvent(new Event(EventType.LEVEL_DIRECTOR_HOSTILITY_REACHED_1));
			this.maxHostilityNotified = 0.2;
		}
	}

	private act(hostility: number, board: Board): void {
		if (!randomChance(hostility)) return;

		const mobCaveProbability = LevelDirector.getMobCaveProbability(hostility);

		if (this.onlySpawnCaves || randomChance(mobCaveProbability)) {
			this.spawnMobCave(hostility, board);
		} else {
			this.spawnPortal(board);
		}
	}

	private static getMobCaveProbability(hostility: number): number {
		if (hostility <= 0.6) return 1 - hostility * 1.2;
		return 0.3;
	}

	private static getNumberOfMobsToSpawn(hostility: number): number {
		if (hostility < 0.2) return 4;
		if (hostility < 0.4) return 7;
		if (hostility < 0.6) return 10;
		if (hostility < 0.8) return 15;
		return 20;
	}

	private static getMobToSpawnInCave(hostility: number): Mob {
		const random = Math.random();

		if (hostility < 0.2) {
			return new Squid(new XY(0, 0));
		} else if (hostility < 0.4) {
			if (random <= 0.6) return new Squid(new XY(0, 0));
			return new Horned(new XY(0, 0));
		} else if (hostility < 0.6) {
			if (random < 0.5) return new Squid(new XY(0, 0));
			if (random < 0.9) return new Horned(new XY(0, 0));
			return new Slugant(new XY(0, 0));
		} else if (hostility < 0.8) {
			if (random < 0.1) return new Squid(new XY(0, 0));
			if (random < 0.6) return new Horned(new XY(0, 0));
			return new Slugant(new XY(0, 0));
		} else {
			if (random < 0.2) return new Horned(new XY(0, 0));
			return new Slugant(new XY(0, 0));
		}
	}

	private static getAllMobsForCave(hostility: number): Mob[] {
		const count = LevelDirector.getNumberOfMobsToSpawn(hostility);
		const mobs: Mob[] = [];
		for (let i = 0; i < count; i++) {
			mobs.push(LevelDirector.getMobToSpawnInCave(hostility));
		}
		return mobs;
	}

	private spawnMobCave(hostility: number, board: Board): void {
		const possiblePositions = LevelDirector.getPossiblePositionsForMobCave(board);
		if (possiblePositions.length === 0) return;

		const chosenPosition = randomElement(possiblePositions);
		if (!chosenPosition) return;

		// Find and remove the Rock at this position
		const crud = board.getThingsAt(chosenPosition).find((t) => t instanceof Rock);
		if (crud) {
			board.removeAtEndOfTurn(crud);
		}

		const cave = new RockMobCave(chosenPosition);
		cave.addMob(new Horned(new XY(0, 0))); // First mob is always a horned (digger)
		cave.addMobs(LevelDirector.getAllMobsForCave(hostility));
		board.addThing(cave);
		board.addEffect(new Hit(chosenPosition));
	}

	private static getPossiblePositionsForMobCave(board: Board): XY[] {
		const positions = new Set<string>();
		const results: XY[] = [];

		const addPos = (xy: XY) => {
			const key = `${xy.x},${xy.y}`;
			if (!positions.has(key)) {
				positions.add(key);
				results.push(xy);
			}
		};

		// Scan horizontally
		for (let y = 0; y < board.height; y++) {
			let currentPassable = false;
			let nextPassable = board.isPassable(new XY(0, y));

			for (let x = 0; x < board.width; x++) {
				const xy = new XY(x, y);
				const nextXY = new XY(x + 1, y);
				const previousPassable = currentPassable;
				currentPassable = nextPassable;
				nextPassable = board.isInBounds(nextXY) && board.isPassable(nextXY);

				const hasRock = board.getThingsAt(xy).some((t) => t instanceof Rock);
				if (hasRock && (previousPassable || nextPassable)) addPos(xy);
			}
		}

		// Scan vertically
		for (let x = 0; x < board.width; x++) {
			let currentPassable = false;
			let nextPassable = board.isPassable(new XY(x, 0));

			for (let y = 0; y < board.height; y++) {
				const xy = new XY(x, y);
				const nextXY = new XY(x, y + 1);
				const previousPassable = currentPassable;
				currentPassable = nextPassable;
				nextPassable = board.isInBounds(nextXY) && board.isPassable(nextXY);

				const hasRock = board.getThingsAt(xy).some((t) => t instanceof Rock);
				if (hasRock && (previousPassable || nextPassable)) addPos(xy);
			}
		}

		return results;
	}

	private spawnPortal(board: Board): void {
		for (let attempts = 0; attempts <= 100; attempts++) {
			const xy = LevelDirector.getRandomEmptyCell(board);
			if (!xy) continue;
			if (board.player && xy.distanceTo(board.player.xy) <= 6) continue;

			const portal = Portal2.create(board, xy);
			if (!portal) continue;

			portal.setSpawnProbability(0.15);
			portal.gold = board.spendGoldBudget(portal.getBaseGoldValue());
			if (this.mobSpawnerConfig) {
				portal.setMobSpawnerConfig(this.mobSpawnerConfig);
			}
			board.addEffect(new CircleEffect(xy));
			for (const neighborXY of xy.getNeighbors()) {
				if (board.isInBounds(neighborXY)) {
					board.addEffect(new CircleEffect(neighborXY));
				}
			}
			return;
		}
	}

	private static getRandomEmptyCell(board: Board): XY | null {
		for (let attempts = 0; attempts < 100; attempts++) {
			const x = randomInt(0, board.width - 1);
			const y = randomInt(0, board.height - 1);
			const xy = new XY(x, y);
			if (board.isPassable(xy) && !board.hasMob(xy)) return xy;
		}
		return null;
	}

	getMobSpawnerConfig(): MobSpawnerConfig | null {
		return this.mobSpawnerConfig;
	}

	setMobSpawnerConfig(config: MobSpawnerConfig): void {
		this.mobSpawnerConfig = config;
	}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			runAtTurnCount: this.runAtTurnCount,
			isFirstRun: this.isFirstRun,
			maxHostilityNotified: this.maxHostilityNotified,
			onlySpawnCaves: this.onlySpawnCaves,
			mobSpawnerConfig: this.mobSpawnerConfig?.serialize() ?? null,
		};
	}

	static deserialize(data: any): LevelDirector {
		const d = new LevelDirector();
		d.runAtTurnCount = data.runAtTurnCount ?? 0;
		d.isFirstRun = data.isFirstRun ?? true;
		d.maxHostilityNotified = data.maxHostilityNotified ?? 0;
		d.onlySpawnCaves = data.onlySpawnCaves ?? false;
		if (data.mobSpawnerConfig) d.mobSpawnerConfig = MobSpawnerConfig.deserialize(data.mobSpawnerConfig);
		return d;
	}
}
